import { Component } from './component.js';
import { VariantSelectedEvent, VariantUpdateEvent } from './events.js';
import { morph } from './morph.js';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('./component.js').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    if (!this.dataset.productId) {
      const idMatch = document.querySelector('[id^="ProductJson-"]')?.id?.match(/ProductJson-(\d+)/);
      if (idMatch) this.dataset.productId = idMatch[1];
    }
    this.applyExistenceVisibility();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    this.applyExistenceVisibility();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /** Cache variants from product JSON once */
  getProductVariants() {
    if (this._variants) return this._variants;
    const jsonEl = document.getElementById('ProductJson-' + this.dataset.productId) ||
                   document.querySelector('[id^="ProductJson-"]');
    if (!jsonEl) return this._variants = [];
    try {
      const product = JSON.parse(jsonEl.textContent);
      return this._variants = Array.isArray(product?.variants) ? product.variants : [];
    } catch (e) {
      console.warn('VariantPicker: failed to parse product JSON', e);
      return this._variants = [];
    }
  }

  /** Current selection, keyed by position (1..3) => value string */
  getCurrentSelection() {
    const selection = {};
    this.querySelectorAll('fieldset.variant-option[data-option-position]').forEach(fs => {
      const pos = Number(fs.dataset.optionPosition);
      const checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) selection[pos] = checked.value;
    });
    return selection;
  }

  /**
   * For a given option position, return:
   * - allowed: Set of values that map to at least one real variant (ignoring inventory)
   * - oos: Set of values where all matching real variants are out of stock (variant.available === false)
   */
  computeOptionState(position) {
    const variants = this.getProductVariants();
    const current = this.getCurrentSelection();
    const otherPositions = [1,2,3].filter(p => p !== position);

    const allowed = new Set();
    const oos = new Set();
    const seen = new Map(); // value -> { hasAny: bool, hasAvailable: bool }

    variants.forEach(v => {
      // Must match other selected options (if any)
      const matchesOthers = otherPositions.every(p => !current[p] || v['option' + p] === current[p]);
      if (!matchesOthers) return;

      const val = v['option' + position];
      if (!val) return;

      const rec = seen.get(val) || { hasAny: false, hasAvailable: false };
      rec.hasAny = true;
      if (v.available) rec.hasAvailable = true;
      seen.set(val, rec);
    });

    for (const [val, rec] of seen.entries()) {
      if (rec.hasAny) {
        allowed.add(val);
        if (!rec.hasAvailable) oos.add(val); // exists but all matches are OOS
      }
    }

    return { allowed, oos };
  }

  /** Hide only non-existent; keep OOS visible (disabled) */
  applyExistenceVisibility() {
    const groups = this.querySelectorAll('fieldset.variant-option[data-option-position]');
    groups.forEach((fs) => {
      const pos = Number(fs.dataset.optionPosition);
      const { allowed, oos } = this.computeOptionState(pos);

      // Map inputs -> labels (your structure wraps input inside label)
      const inputs = fs.querySelectorAll('input[type="radio"]');
      inputs.forEach((input) => {
        const label = input.closest('.variant-option__button-label') || input.parentElement;
        const val = input.value;

        const exists = allowed.has(val);
        const isOOS = oos.has(val);

        // Hide values that can never produce a variant
        if (!exists) {
          if (label) label.hidden = true;
          input.hidden = true;
          input.disabled = true;

          // If the currently selected value became invalid, unselect it
          if (input.checked) {
            input.checked = false;
          }
          return;
        }

        // Exists: ensure visible
        if (label) label.hidden = false;
        input.hidden = false;

        // Out of stock? keep visible but disabled + style
        input.disabled = isOOS;
        input.setAttribute('aria-disabled', String(isOOS));
        if (label) label.classList.toggle('is-oos', isOOS);
      });

      // If nothing valid is selected in this group, auto-select first existing (even if OOS, we try to pick an in-stock if possible)
      const anyCheckedStillValid = Array.from(inputs).some(i => i.checked && !i.hidden);
      if (!anyCheckedStillValid) {
        const firstInStock = Array.from(inputs).find(i => !i.hidden && !i.disabled);
        const fallback = firstInStock || Array.from(inputs).find(i => !i.hidden);
        if (fallback && !fallback.checked) {
          fallback.checked = true;
          fallback.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    // one-off CSS
    if (!document.getElementById('variant-oos-style')) {
      const style = document.createElement('style');
      style.id = 'variant-oos-style';
      style.textContent = `
        .variant-option__button-label[hidden] { display:none !important; }
        .variant-option__button-label.is-oos { opacity:.5; pointer-events:none; }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
